<template>

  <div>
    <Modal
      :title="$t('titles.edit_blackout')"
      :submit="canEditBlackout ? submit : undefined"
      :submit-button="canEditBlackout"
      :submitting="submitting"
    >

      <o-text-field
        v-if="$can('organizations.manage')"
        :label="$t('titles.organization')"
        readonly
        :model-value="blackout.location && blackout.location.organization ? blackout.location.organization.name : t('misc.unknown_item')"
      />

      <o-text-field
        :label="$t('titles.location')"
        readonly
        :model-value="blackout.location ? blackout.location.locationName : t('misc.unknown_item')"
      />

      <o-text-field
        v-if="canEditBlackout"
        :label="$t('titles.name')"
        v-model="v$.name.$model"
        :validator="v$.name"
        maxlength="50"
        required
      />

      <o-text-field
        v-if="!canEditBlackout"
        :label="$t('titles.name')"
        readonly
        :model-value="v$.name.$model"
      />

      <v-row>

        <v-col cols="12" md="6" class="pb-0 pb-md-4">

          <o-date-time-field
            v-if="canEditBlackout"
            :date-label="$t('titles.start_date')"
            :time-label="$t('titles.start_time')"
            v-model="v$.startDate.$model"
            :validator="v$.startDate"
            :step="30"
            required
          />

          <o-text-field
            v-if="!canEditBlackout"
            :label="$t('titles.start_date')"
            readonly
            :model-value="Temporal.PlainDateTime.from(v$.startDate.$model).toLocaleString('en', {year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric'})"
          />

        </v-col>

        <v-col cols="12" md="6" class="pt-0 pt-md-4">

          <o-date-time-field
            v-if="canEditBlackout"
            :date-label="$t('titles.end_date')"
            :time-label="$t('titles.end_time')"
            v-model="v$.endDate.$model"
            :validator="v$.endDate"
            :step="30"
            required
          />

          <o-text-field
            v-if="!canEditBlackout"
            :label="$t('titles.end_date')"
            readonly
            :model-value="Temporal.PlainDateTime.from(v$.endDate.$model).toLocaleString('en', {year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric'})"
          />

        </v-col>

      </v-row>

      <o-select      
        :label="$t('titles.channels_one_or_more')"
        :model-value="blackout.channels"
        :items="ChannelRegistry.getOptions()"
        multiple
        readonly
      />

    </Modal>
  </div>

</template>

<script setup lang="ts">

import { computed, inject, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

// components
import Modal from '../../Modal.vue'
// @ts-ignore: don't complain about missing typings
import ODateTimeField from '../../forms/ODateTimeField.vue'

// models
import Blackout from '../../../models/Blackout'

// validation
import useVuelidate from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'
import { required, minLength, maxLength } from '../../../utils/i18n-validators'

// misc
// @ts-ignore
import { closeDialog } from 'vue3-promise-dialog'
import NotificationService from '../../../services/NotificationService'
import ChannelRegistry from '../../../channels/ChannelRegistry'
import {Temporal} from '@js-temporal/polyfill'
import Location from '../../../models/Location'
import type { Can } from '../../../main'

const props = defineProps({
  
  blackout: {
    type: Blackout,
    required: true,
  },

})

const $can = inject('$can') as Can
const { t } = useI18n()

const canEditBlackout = computed<boolean>(() => {
  return $can('blackouts.write', props.blackout.locationID)
})

/**
 * Form state and rules
 */

const formState = reactive({
  name: props.blackout.name,
  // the API already emits and consumes datetimes in HTML5 datetime-local format, so there's no need to convert
  startDate: props.blackout.startDate,
  endDate: props.blackout.endDate,
})

// custom validators

const nowInFacilityTimeZone = computed<Temporal.PlainDateTime>(() => {
  const location = Location.find(props.blackout.locationID)
  return Temporal.Now.zonedDateTimeISO(location!.timezone).toPlainDateTime()
})

const isAfterStart = (value: any) => {
  if (!helpers.req(value) || !formState.startDate) {
    return true
  }
  const startDate = Temporal.PlainDateTime.from(formState.startDate)
  const thisDate = Temporal.PlainDateTime.from(value)
  return Temporal.PlainDateTime.compare(startDate, thisDate) === -1
}

const isBeforeEnd = (value: any) => {
  if (!helpers.req(value) || !formState.endDate) {
    return true
  }
  const endDate = Temporal.PlainDateTime.from(formState.endDate)
  const thisDate = Temporal.PlainDateTime.from(value)
  return Temporal.PlainDateTime.compare(endDate, thisDate) === 1
}

const isNotInPast = (value: any) => {

  if (!helpers.req(value)) {
    return true
  }

  const then = Temporal.PlainDateTime.from(value)
  return Temporal.PlainDateTime.compare(nowInFacilityTimeZone.value, then) === -1

}

const isNoMoreThanOneHourInThePast = (value: any) => {

  if (!helpers.req(value)) {
    return true
  }

  const then = Temporal.PlainDateTime.from(value)
  const delta = then.until(nowInFacilityTimeZone.value)
  return delta.total('seconds') <= 3600

}

const isOnHalfHour = (value: any) => {
  if (!value) {
    return true
  }
  const date = Temporal.PlainDateTime.from(value)
  return (date.minute === 0 || date.minute === 30) && date.second === 0
}

const isNoLongerThan60DaysAfterStartDate = (value: any) => {
  return (
    !helpers.req(value) ||
    !formState.startDate ||
    Temporal.PlainDateTime.from(formState.startDate).until(Temporal.PlainDateTime.from(value)).days <= 60
  )
}

const formRules = {
  name: {
    required,
    minLength: minLength(3),
    maxLength: maxLength(50),
  },
  startDate: {
    required,
    isBeforeEnd: helpers.withMessage(
      t('validations.earlier_than_end_date'),
      isBeforeEnd
    ),
    isNoMoreThanOneHourInThePast: helpers.withMessage(
      t('validations.not_more_than_one_hour_in_past'),
      isNoMoreThanOneHourInThePast
    ),
    isOnHalfHour: helpers.withMessage(
      t('validations.on_half_hour'),
      isOnHalfHour
    ),
  },
  endDate: {
    required,
    isAfterStart: helpers.withMessage(
      t('validations.later_than_start_date'),
      isAfterStart
    ),
    isNotInPast: helpers.withMessage(
      t('validations.not_in_past'),
      isNotInPast
    ),
    isOnHalfHour: helpers.withMessage(
      t('validations.on_half_hour'),
      isOnHalfHour
    ),
    isNoLongerThan60DaysAfterStart: helpers.withMessage(
      t('validations.no_more_than_60_days_after_start'),
      isNoLongerThan60DaysAfterStartDate
    ),
  },
}

const v$ = useVuelidate(formRules, formState)

/**
 * Submit
 */

const submitting = ref<boolean>(false)

async function submit() {

  const isValid = await v$.value.$validate()

  if (!isValid) {
    return
  }

  submitting.value = true

  const payload = { ...props.blackout, ...formState }

  Blackout.save(payload, !!payload.ID)
    .then(() => {
      NotificationService.success(t('alerts.blackout_updated'))
      closeDialog(null)
    })
    .catch((error) => {
      console.error(error)
      const msg = t('alerts.couldnt_update_blackout')
      NotificationService.error(msg)
    })
    .finally(() => {
      submitting.value = false
    })

}

</script>
